import { type ComponentProps, useCallback, useState } from 'react';

import type { HTMLProps } from '@base-ui/react';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Popover } from '@opendoor/atoms/popover';
import { IconBookmark } from '@tabler/icons-react';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';

import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { SavedSearchesMenu } from './SavedSearchesMenu';
import { useSavedSearchesMenu } from './useSavedSearchesMenu';

export interface SavedSearchesDropdownProps {
  /**
   * Applies the selected saved search. The host page owns the marketplace
   * search state, so it decides whether to refetch results in place (same
   * market) or hard-navigate for fresh SSR data (different market / zone /
   * polygon).
   */
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
  /**
   * The saved search currently applied on the page, if any. Its row gets an
   * "Active" marker.
   */
  activeSavedSearchId?: string | null;
  /**
   * Opens the filters panel. Backs the empty state's CTA so a user with no
   * saved searches is pointed at where saving starts.
   */
  onOpenFilters?: () => void;
  /** Size for the trigger button; match the surrounding controls. */
  size?: ComponentProps<typeof Button>['size'];
}

/**
 * Saved-searches switcher. Renders for logged-in users — including with zero
 * saved searches, where it teaches the feature instead of hiding (an invisible
 * feature can't be discovered). Desktop gets an anchored popover; small
 * screens get the bottom-sheet dialog.
 */
export const SavedSearchesDropdown = ({
  onApplySavedSearch,
  activeSavedSearchId,
  onOpenFilters,
  size = 'lg',
}: SavedSearchesDropdownProps) => {
  const {
    isLoggedIn,
    savedSearches,
    pendingDelete,
    requestDelete,
    cancelDelete,
    rename,
    confirmDelete,
  } = useSavedSearchesMenu();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      onApplySavedSearch(savedSearch);
      setIsOpen(false);
    },
    [onApplySavedSearch],
  );

  const handleOpenFilters = useCallback(() => {
    setIsOpen(false);
    onOpenFilters?.();
  }, [onOpenFilters]);

  const count = savedSearches.length;
  const buttonLabel = count > 0 ? `Saved searches (${count})` : 'Saved searches';
  const renderTriggerButton = useCallback(
    (props: HTMLProps) => (
      <Button
        {...props}
        analyticsName="saved-searches-dropdown-trigger"
        aria-label={buttonLabel}
        size={size}
        variant="secondary"
      >
        <IconBookmark size={18} aria-hidden />
        {buttonLabel}
      </Button>
    ),
    [buttonLabel, size],
  );

  if (!isLoggedIn) {
    return null;
  }

  const menuContent = (
    <SavedSearchesMenu
      savedSearches={savedSearches}
      activeSavedSearchId={activeSavedSearchId}
      onSelect={handleSelect}
      onRename={rename}
      onDelete={requestDelete}
      onOpenFilters={onOpenFilters ? handleOpenFilters : undefined}
    />
  );

  return (
    <>
      {isDesktop ? (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger render={renderTriggerButton} />
          <Popover align="start" sideOffset={16} className="w-80 max-w-[calc(100vw-2rem)]">
            {menuContent}
          </Popover>
        </Popover.Root>
      ) : (
        <>
          <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Trigger render={renderTriggerButton} />
            <Dialog title="Your saved searches">{menuContent}</Dialog>
          </Dialog.Root>
        </>
      )}
      {pendingDelete && (
        <ConfirmDeleteDialog
          savedSearch={pendingDelete}
          isOpen={pendingDelete !== null}
          onClose={cancelDelete}
          onConfirmDelete={confirmDelete}
        />
      )}
    </>
  );
};
